// @flow
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Divider,
  Hidden,
  makeStyles,
  Typography,
} from '@material-ui/core';

import {colors} from '../../../../assets/styles/colors';

import FilterButton from './FilterButton/FilterButton';

import {commaFormat} from '../../../../utils/number';

import type {Currency} from '../../../../entities';
import type {DashboardAction, OrderCustomer} from '../../duck';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {Link} from 'react-router-dom';
type Props = {
  customers?: OrderCustomer[],
  currency?: Currency,
  isFetchingCustomers?: boolean,
  buildCommonQueryParamsCallback: () => any,
  fetchSummaryOrderCustomers: (payload: DashboardAction) => void,
};

const useStyles = makeStyles(theme => ({
  section: {
    height: '100%',
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.grey900,
  },
  divider: {
    backgroundColor: '#E8E8ED',
    margin: theme.spacing(2, 0),
  },
  btnDownload: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  seeMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  seeMore: {
    color: colors.grey800,
    fontSize: 14,
  },
  wrapClients: {
    margin: theme.spacing(1.5, 0),
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(2),
      flexDirection: 'row',
    },
  },
  client: {
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  clientContent: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
    },
  },
  circle: {
    marginRight: theme.spacing(2),
    backgroundColor: colors.purple,
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      marginRight: 0,
    },
  },
  circleText: {
    fontWeight: 600,
    fontSize: 14,
    color: '#FFFFFF',
  },
  name: {
    fontWeight: 600,
    fontSize: 14,
    color: colors.grey900,
    [theme.breakpoints.up('md')]: {
      textAlign: 'center',
    },
  },
  amount: {
    fontWeight: 400,
    fontSize: 12,
    color: colors.grey900,
  },
  orders: {
    fontWeight: 400,
    fontSize: 12,
    color: colors.grey700,
    marginLeft: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginLeft: 0,
    },
  },
  description: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
    },
  },
  advice: {
    fontSize: 14,
    color: colors.grey700,
  },
  containerProgress: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  clientWrap: {
    [theme.breakpoints.up('md')]: {
      width: 'calc(20% - 16px)',
    },
  },
}));

export default function ClientsSection(props: Props) {
  const {
    customers,
    currency,
    isFetchingCustomers,
    buildCommonQueryParamsCallback,
    fetchSummaryOrderCustomers,
  } = props;

  const classes = useStyles();
  const [customerFilter, setCustomerFilter] = useState('total');

  const handleUpdateFilter = (value: string) => {
    if (value) {
      setCustomerFilter(value);
    }
  };

  const buildQueryParamsCallback = useCallback(() => {
    let params: {[key: string]: any} = {
      size: 5,
      page: 0,
    };
    if (!!customerFilter) {
      params.mode = customerFilter;
    }
    return params;
  }, [customerFilter]);

  useEffect(() => {
    if (buildQueryParamsCallback) {
      const dateRange = buildCommonQueryParamsCallback();
      const queryParams = buildQueryParamsCallback();
      if (dateRange && queryParams) {
        const queryFilter = {
          ...queryParams,
          init_date: dateRange.start_date,
          end_date: dateRange.end_date,
        };
        fetchSummaryOrderCustomers({queryFilter});
      }
    }
  }, [
    buildCommonQueryParamsCallback,
    buildQueryParamsCallback,
    fetchSummaryOrderCustomers,
  ]);

  const buildClientsSection = (client: OrderCustomer, idx: number) => {
    return (
      <div key={idx} className={classes.clientWrap}>
        <div className={classes.client}>
          <div className={classes.clientContent}>
            <div className={classes.circle}>
              <Typography className={classes.circleText}>{idx + 1}</Typography>
            </div>
            <div>
              <Typography className={classes.name}>
                {client.fullName}
              </Typography>
              <div className={classes.description}>
                <Typography className={classes.amount}>
                  {commaFormat(
                    client.amount ? client.amount : 0,
                    currency ? currency.symbol : ''
                  )}
                </Typography>
                <Typography className={classes.orders}>
                  {client.numOrders} pedido
                  {client.numOrders && Number(client.numOrders) > 1 ? 's' : ''}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <Hidden mdUp>
          <Divider className={classes.divider} />
        </Hidden>
      </div>
    );
  };
  return (
    <div className={classes.section}>
      <div>
        <div className={classes.header}>
          <Typography className={classes.title}>Mejores Clientes</Typography>
          <Hidden smDown>
            <div>
              <FilterButton
                id="amount"
                text="Monto de venta"
                selected={customerFilter.includes('amount')}
                handleOnClick={handleUpdateFilter}
              />
              <FilterButton
                id="average"
                text="Ticket promedio"
                selected={customerFilter.includes('average')}
                handleOnClick={handleUpdateFilter}
              />
            </div>
          </Hidden>
        </div>
        <Divider className={classes.divider} />
        <Hidden mdUp>
          <div>
            <FilterButton
              id="total"
              text="Monto de venta"
              selected={customerFilter.includes('total')}
              handleOnClick={handleUpdateFilter}
            />
            <FilterButton
              id="average"
              text="Ticket promedio"
              selected={customerFilter.includes('average')}
              handleOnClick={handleUpdateFilter}
            />
          </div>
        </Hidden>
        <div className={classes.wrapClients}>
          {isFetchingCustomers ? (
            <div className={classes.containerProgress}>
              <CircularProgress />
            </div>
          ) : customers && customers.length ? (
            customers.slice(0, 5).map((c, idx) => buildClientsSection(c, idx))
          ) : (
            <Typography className={classes.advice}>
              Genera mas ventas para poder visualizar a tus clientes.
            </Typography>
          )}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.seeMoreContainer}>
          <CustomButton
            component={Link}
            to="/customers"
            className={classes.seeMore}
          >
            Ver detalle
          </CustomButton>
        </div>
      </div>
    </div>
  );
}

ClientsSection.propTypes = {
  customers: PropTypes.array,
  currency: PropTypes.object,
  isFetchingCustomers: PropTypes.bool,
  buildCommonQueryParamsCallback: PropTypes.func.isRequired,
  fetchSummaryOrderCustomers: PropTypes.func.isRequired,
};
